import
{
    CharacterStateBase, RifleAim,
} from './_stateLibrary';
import {ICharacterState} from '../../interfaces/ICharacterState';
import {Character} from '../Character';
import { CharacterAnimation } from "../../enums/CharacterAnimation";
import { RifleIdle } from "./RifleIdle";
import { Npc } from "../Npc";
import * as THREE from 'three';
import { BloodEffect } from "../particles/BloodEffect";
import { FiringEffect } from "../particles/FiringEffect";
import { RifleShootAudio } from './audio/RifleShootAudio';

export class RifleShoot extends CharacterStateBase implements ICharacterState {
    private rayCaster: THREE.Raycaster;
    private blood: BloodEffect;
    private fire: FiringEffect;
    private audio: RifleShootAudio | null = null;

    constructor(character: Character) {
        super(character);
        this.rayCaster = new THREE.Raycaster();

        this.character.simulation.velocitySimulator.damping = 0.6;
        this.character.simulation.velocitySimulator.mass = 10;
        const clip = this.character.animationManager.animations.find(clip => clip.name === CharacterAnimation.RifleShoot)
        const action = this.character.animationManager.mixer.clipAction(clip);
        action.timeScale = 1.4;
        this.character.simulation.setArcadeVelocityTarget(0);
        this.playAnimation(CharacterAnimation.RifleShoot, 0.1);

        if (character.world?.audioManager) {
            this.audio = new RifleShootAudio(character.world.audioManager, character);
            this.audio.play();
        }
    }

    public exit(): void {
        this.audio?.stop();
    }

    public update(timeStep: number): void {
        super.update(timeStep);
        this.fallInAir();
        this.onDie();
        this.handleImpact();
    }

    private handleImpact(): void {
        // Fire muzzle flash at the weapon
        this.fire = new FiringEffect(this.character.world);

        const handWorldPosition = new THREE.Vector3();
        const rifle = this.character.getObjectByName('pistol') as THREE.Object3D | undefined;
        if (rifle) {
            rifle.getWorldPosition(handWorldPosition);
            this.fire.triggerFiringFire(rifle);
        }

        // Raycaster: NPC casts from weapon/world direction, Player from camera center per docs
        if (this.character instanceof Npc) {
            const origin = new THREE.Vector3();

            // Use weapon muzzle (if available) for origin; otherwise character position
            if (rifle) {
                rifle.getWorldPosition(origin);
            } else {
                this.character.getWorldPosition(origin);
            }

            // Use character's forward as the aim direction to avoid weapon-axis misalignment
            const direction = new THREE.Vector3();
            this.character.getWorldDirection(direction).normalize();

            // Offset origin slightly forward to avoid intersecting own hand/weapon
            const epsilon = 0.35; // ~35 cm in front of muzzle
            const shiftedOrigin = origin.clone().add(direction.clone().multiplyScalar(epsilon));

            // Reasonable shooting range
            this.rayCaster.near = 0.05;
            this.rayCaster.far = 200;

            this.rayCaster.set(shiftedOrigin, direction);
        } else {
            // Player uses screen center
            this.rayCaster.setFromCamera(new THREE.Vector2(0, 0), this.character.world.camera);
            this.rayCaster.near = 0;
            this.rayCaster.far = 1000;
        }

        // Intersections: ignore self (weapon/body) and debug helpers to prevent self-hits/noise
        // Restrict raycast targets to characters only to avoid scene occluders
        const characterRoots = this.character.world.characters as unknown as THREE.Object3D[];
        const allIntersects = this.rayCaster.intersectObjects(characterRoots, true);
        const intersects = allIntersects.filter(hit =>
            !this.isDescendantOf(hit.object, this.character as unknown as THREE.Object3D)
            && !this.isDebugHelper(hit.object)
        );

        let targetCharacter: Character | null = null;
        let targetPoint: THREE.Vector3 | null = null;

        if (intersects.length > 0) {
            // Prefer the first intersection that belongs to a Character (excluding the shooter)
            const characterHit = intersects.find(hit => {
                const ch = this.findAncestorCharacter(hit.object);
                return ch !== null && ch !== this.character;
            });

            if (characterHit) {
                targetCharacter = this.findAncestorCharacter(characterHit.object);
                targetPoint = characterHit.point.clone();
            }
        }

        // Fallback: sphere intersection against all other characters to handle inside/edge cases
        if (!targetCharacter) {
            const origin = this.rayCaster.ray.origin;
            let bestDist = Infinity;
            let bestChar: Character | null = null;
            let bestPoint: THREE.Vector3 | null = null;

            for (const ch of this.character.world.characters) {
                if (ch === this.character) continue;
                const center = new THREE.Vector3();
                ch.getWorldPosition(center);
                center.y += 0.9; // chest height approx
                const sphere = new THREE.Sphere(center, 0.6);
                const tmp = new THREE.Vector3();
                const p = this.rayCaster.ray.intersectSphere(sphere, tmp);
                if (p) {
                    const d = p.distanceTo(origin);
                    if (d < bestDist && d >= this.rayCaster.near && d <= this.rayCaster.far) {
                        bestDist = d;
                        bestChar = ch;
                        bestPoint = p.clone();
                    }
                }
            }

            if (bestChar && bestPoint) {
                targetCharacter = bestChar;
                targetPoint = bestPoint;
            }
        }

        if (targetPoint) {
            // Visual debug helper: line from ray origin to hit point + small sphere at impact
            // this.spawnRayDebug(this.rayCaster.ray.origin, targetPoint);
        }
        if (targetCharacter && targetCharacter !== this.character && targetPoint && !targetCharacter.isDead) {
            this.blood = new BloodEffect(this.character.world);
            this.blood.spawn(targetCharacter, targetPoint);
            targetCharacter.takeDamage(50);

            if (this.character === (this.character.world.player as unknown as Character)) {
                this.character.world.crimeDetectionService?.reportCrime({ position: targetPoint, perpetrator: this.character });
            }
        }
    }

    // Visual helper to debug ray hits
    private spawnRayDebug(origin: THREE.Vector3, hitPoint: THREE.Vector3): void {
        const world = this.character.world;

        // Line from origin to hit
        const positions = new Float32Array([
            origin.x, origin.y, origin.z,
            hitPoint.x, hitPoint.y, hitPoint.z,
        ]);
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        const mat = new THREE.LineBasicMaterial({ color: 0x00ff00 });
        const line = new THREE.Line(geom, mat);
        line.renderOrder = 999;
        // Mark as debug so raycaster filter can ignore
        (line.userData as any).__debugRayHelper = true;
        world.graphicsWorld.add(line);

        // Impact marker
        const sphereGeom = new THREE.SphereGeometry(0.05, 8, 8);
        const sphereMat = new THREE.MeshBasicMaterial({ color: 0xff0000, depthTest: false });
        const marker = new THREE.Mesh(sphereGeom, sphereMat);
        marker.position.copy(hitPoint);
        marker.renderOrder = 1000;
        (marker.userData as any).__debugRayHelper = true;
        world.graphicsWorld.add(marker);

        // Auto dispose after a short delay
        window.setTimeout(() => {
            world.graphicsWorld.remove(line);
            world.graphicsWorld.remove(marker);
            geom.dispose();
            mat.dispose();
            sphereGeom.dispose();
            sphereMat.dispose();
        }, 1500);
    }

    // Utility: determine if the object (or its parents) is a debug helper
    private isDebugHelper(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if ((current.userData as any)?.__debugRayHelper) return true;
            current = current.parent as THREE.Object3D | null;
        }
        return false;
    }

    // Utility: true if `object` is part of `ancestor` subtree
    private isDescendantOf(object: THREE.Object3D, ancestor: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current === ancestor) return true;
            current = current.parent as THREE.Object3D | null;
        }
        return false;
    }

    // Utility: climb parents to find an Npc, if any
    private findAncestorCharacter(object: THREE.Object3D): Character | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current instanceof Character) return current as unknown as Character;
            current = current.parent as THREE.Object3D | null;
        }
        return null;
    }

    public onInputChange(): void {
        super.onInputChange();

        if (this.character.actions.shoot.justReleased) {
            this.character.setState(new RifleAim(this.character));
        }

        if (this.character.actions.aim.justReleased) {
            this.character.setState(new RifleIdle(this.character));
        }
    }
}
